/**
 * Launch-cohort tagging for retention analytics.
 *
 * Captures where a visitor came from (UTM params) and which compute-tool door
 * they landed on (`launchTool`), persists it, and exposes it as PostHog-style
 * super properties so every downstream event can be attributed to its cohort.
 *
 * Pure where it can be: `parseCohort` is deterministic — no Date.now / random,
 * no I/O. Persistence helpers are SSR-safe (guarded on `typeof window`) and
 * never throw, so they are safe to call from any surface during hydration.
 */

const STORAGE_KEY = 'bs_cohort'

export interface Cohort {
  source?: string
  medium?: string
  campaign?: string
  content?: string
  term?: string
  launchTool?: string
}

/** Map of query param name → Cohort key. */
const PARAM_MAP: ReadonlyArray<readonly [param: string, key: keyof Cohort]> = [
  ['utm_source', 'source'],
  ['utm_medium', 'medium'],
  ['utm_campaign', 'campaign'],
  ['utm_content', 'content'],
  ['utm_term', 'term'],
  ['launchTool', 'launchTool'],
]

/** Map of Cohort key → super-property name (for posthog.register). */
const SUPER_PROP_MAP: ReadonlyArray<readonly [key: keyof Cohort, prop: string]> = [
  ['source', 'utm_source'],
  ['medium', 'utm_medium'],
  ['campaign', 'utm_campaign'],
  ['content', 'utm_content'],
  ['term', 'utm_term'],
  ['launchTool', 'launch_tool'],
]

/**
 * Parse a query string into a Cohort. Pure and deterministic.
 *
 * Accepts the query string with or without a leading '?'. Absent keys are
 * omitted (never set to undefined/empty). Empty values are treated as absent.
 */
export function parseCohort(search: string): Cohort {
  const cohort: Cohort = {}
  if (!search) return cohort

  const query = search.startsWith('?') ? search.slice(1) : search
  if (!query) return cohort

  const params = new URLSearchParams(query)
  for (const [param, key] of PARAM_MAP) {
    const value = params.get(param)
    if (value !== null && value !== '') {
      cohort[key] = value
    }
  }
  return cohort
}

/** True when the cohort carries at least one attributable signal. */
export function hasCohort(c: Cohort): boolean {
  return SUPER_PROP_MAP.some(([key]) => c[key] !== undefined && c[key] !== '')
}

/**
 * Flatten a Cohort to PostHog super properties, only for present keys.
 * e.g. { source: 'fb' } → { utm_source: 'fb' }.
 */
export function cohortSuperProps(c: Cohort): Record<string, string> {
  const props: Record<string, string> = {}
  for (const [key, prop] of SUPER_PROP_MAP) {
    const value = c[key]
    if (value !== undefined && value !== '') {
      props[prop] = value
    }
  }
  return props
}

/**
 * Persist a cohort to localStorage. SSR-safe and never throws (storage can be
 * unavailable or full in private-browsing modes).
 */
export function persistCohort(c: Cohort): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(c))
  } catch {
    // Storage unavailable / quota exceeded — non-fatal, attribution is best-effort.
  }
}

/**
 * Read the persisted cohort. SSR-safe and never throws; returns null when
 * absent, unreadable, or malformed.
 */
export function getCohort(): Cohort | null {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return null
    }
    return parsed as Cohort
  } catch {
    return null
  }
}

/**
 * First-touch launch-cohort capture. Persists the cohort parsed from `search`
 * ONLY if none is stored yet, so the original acquisition source (the door the
 * owner first came through) is never overwritten by a later in-app navigation.
 * Returns the effective (now-persisted) cohort.
 *
 * `fallbackLaunchTool` covers the common case an explicit `?launchTool=` param
 * doesn't: a visitor landing directly on a tool page (search result, shared
 * link, magazine cross-link) with no query string at all. Only used when the
 * query string carries no `launchTool` of its own, so an explicit param always
 * wins. Callers resolve this from the current pathname against the blade
 * registry — this module stays free of that dependency.
 *
 * Call this once, at boot, before analytics init so the cohort can be attached
 * as super properties to every subsequent event. SSR-safe; never throws.
 */
export function captureLaunchCohort(search: string, fallbackLaunchTool?: string): Cohort {
  const existing = getCohort()
  if (existing && hasCohort(existing)) return existing

  const fresh = parseCohort(search)
  if (!fresh.launchTool && fallbackLaunchTool) {
    fresh.launchTool = fallbackLaunchTool
  }
  if (hasCohort(fresh)) {
    persistCohort(fresh)
    return fresh
  }
  return existing ?? {}
}
